/**
 * 69.49 step 1 (ARCHITECTURE §5.30; DECISIONS 273, the owner: "A: yep b feeding c, this will tie into the larger economy so
 * feeding b will be the available resources that can be committed to the fight … For the quickmatch we're still just mocking
 * that stuff. B equal by default … C those 5 work … D truth by default"): what a side fields, composed — a budget (points)
 * spent by a doctrine's shopping list, re-weighted by what the side expects of the enemy (the truth: the enemy's own
 * composition). Its output is today's counts, per side; everything downstream (the setup's parking, the bots) as before.
 * - **The budget** comes from a source (`ForceBudgets`) the economy fills later — the forces close enough, owned and in
 *   transit reach, that can be committed to the fight; the quick match mocks it: FORCE_BUDGET.base × each side's factor
 *   (equal by default; a skew dial for asymmetric matches).
 * - **The doctrine:** balanced, armour, mobile, light or air (273 (c)); the commander's style names one by default
 *   (`doctrineOfStyle`). A side with no doctrine (null) fields the setup's own counts: the authored force (A), the override.
 * - **The expectation** (273 (d), truth by default): each side composed blind first, then again against the other's first
 *   composition — the missile technical only against ships, the rocket technical and the tank weighted up against armour.
 * - **Crews count against the troops** (lane E's 69.61 finding: at 12 a side the tank, the technical and the mortar carrier
 *   each crew a squad, so 3 of 4 squads sat parked): a crewed vehicle (a tank, a technical, a mortar carrier) takes one of the
 *   side's squads, at most FORCE_BUDGET.crewShare of them; a carrier (the 6×6, the tracked .50) is ridden by a squad on
 *   foot, at most one a squad left.
 * - **No troops yet** (273 (e): later, with 256's roles); no misled expectation (273 (d): later, with strategic intel).
 * Pure and deterministic: a function of the budgets, the doctrines and the troops; no seed is needed.
 */
import type { MountArmament } from '../types';

export type Doctrine = 'balanced' | 'armour' | 'mobile' | 'light' | 'air';
export const DOCTRINES: readonly Doctrine[] = ['balanced', 'armour', 'mobile', 'light', 'air'];

/** What the composer buys (a technical by its mount; the tracked carrier as the .50 or the mortar). */
export type ForceKind = 'transport' | 'tracked' | 'mortarCarrier' | 'tank' | 'technical' | 'rocketTechnical' | 'missileTechnical' | 'bike' | 'hmg' | 'airstrike' | 'dropship' | 'pod';
/** The buying order's tie-break (the heaviest first). */
const KINDS: readonly ForceKind[] = ['tank', 'tracked', 'mortarCarrier', 'transport', 'technical', 'rocketTechnical', 'missileTechnical', 'bike', 'hmg', 'airstrike', 'dropship', 'pod'];
/** The vehicles: a spare of each in the motor pool (vehicleStock) costs its price again. */
const VEHICLES: ReadonlySet<ForceKind> = new Set<ForceKind>(['transport', 'tracked', 'mortarCarrier', 'tank', 'technical', 'rocketTechnical', 'missileTechnical', 'bike']);
/** A standing crew: one of the side's squads each (ai/technicalLoop.ts technicalCrews). */
const CREWED: ReadonlySet<ForceKind> = new Set<ForceKind>(['tank', 'mortarCarrier', 'technical', 'rocketTechnical', 'missileTechnical']);
/** Ridden by a squad on foot (its driver one of the riders). */
const CARRIERS: ReadonlySet<ForceKind> = new Set<ForceKind>(['transport', 'tracked']);
const CALLS: ReadonlySet<ForceKind> = new Set<ForceKind>(['airstrike', 'dropship', 'pod']);

/** §5.30's prices (points; a proposal to tune by sweeps later). */
export const FORCE_PRICE: Readonly<Record<ForceKind, number>> = {
  transport: 2, tracked: 3, mortarCarrier: 4, tank: 5, technical: 2, rocketTechnical: 3, missileTechnical: 3, bike: 1, hmg: 1, airstrike: 3, dropship: 4, pod: 3,
};

/** Each doctrine's shopping list: the weights it buys by (a kind's count over its weight, the lowest bought next). */
export const DOCTRINE_WEIGHTS: Readonly<Record<Doctrine, Partial<Record<ForceKind, number>>>> = {
  balanced: { transport: 2, tank: 1, technical: 1, mortarCarrier: 1, rocketTechnical: 0.5, missileTechnical: 0.5, airstrike: 1, dropship: 0.5 },
  armour: { tank: 3, tracked: 2, transport: 1, mortarCarrier: 0.5, rocketTechnical: 0.5 },
  mobile: { technical: 2, transport: 2, bike: 2, rocketTechnical: 1, missileTechnical: 1 },
  light: { mortarCarrier: 1, hmg: 2, airstrike: 1, pod: 1, transport: 0.5 },
  air: { dropship: 2, pod: 2, airstrike: 2, missileTechnical: 1, transport: 0.5 },
};

/**
 * The dials: the quick match's mock budget a side (§5.30: ~12 a small combined force; the Story preset's mirror is 13), the
 * share of a side's squads that may crew, the calls' and the bikes' and HMGs' caps.
 */
export const FORCE_BUDGET = { base: 13, crewShare: 0.5, callsMax: 4, bikesPerSquad: 1, hmgsPerSquad: 1 };

/** 273 (c): the commander's style names its doctrine by default (a proposal): focus armour, spread mobile, else balanced. */
export function doctrineOfStyle(style: string | undefined): Doctrine {
  return style === 'focus' ? 'armour' : style === 'spread' ? 'mobile' : 'balanced';
}

/** The budget source (273 (a): the economy fills it later — what can be committed to the fight). */
export interface ForceBudgets { budgetFor(team: number): number }
/** The quick match's mock: FORCE_BUDGET.base × each side's factor (equal by default; the skew dial). */
export function mockBudgets(factors: readonly number[] = [], base = FORCE_BUDGET.base): ForceBudgets {
  return { budgetFor: (team: number) => base * (factors[team] ?? 1) };
}

/** A side's force in the setup's terms: its counts (the setup's per-team rows, for one side). */
export interface SideForce {
  transports: number; tracked: number; trackedArmaments?: readonly MountArmament[]; tanks: number; technicals: number; technicalArmaments?: readonly MountArmament[];
  bikes: number; hmgs: number; airstrikes: number; dropships: number; pods: number;
}
/** A composed side: its counts, what it bought and spent. */
export interface ComposedForce extends SideForce {
  trackedArmaments: MountArmament[]; technicalArmaments: MountArmament[];
  bought: Partial<Record<ForceKind, number>>; spent: number; budget: number; doctrine: Doctrine;
}

/** What a side expects of the enemy (273 (d): the truth, its composition). */
export interface Expectation { air: boolean; armour: boolean }
const expects = (f: Partial<Record<ForceKind, number>>): Expectation => ({ air: (f.dropship ?? 0) > 0, armour: (f.tank ?? 0) + (f.tracked ?? 0) > 0 });

/** The doctrine's weights re-weighted by the expectation (none: composed blind — no missile technical, nothing against armour). */
function weights(doctrine: Doctrine, expect: Expectation | null): Partial<Record<ForceKind, number>> {
  const w = { ...DOCTRINE_WEIGHTS[doctrine] };
  // (258: the missile technical only where ships fly — against the enemy's; none composed blind)
  if (!expect?.air) delete w.missileTechnical;
  else w.missileTechnical = (w.missileTechnical ?? 0) + 1;
  // (against armour the rocket technical, and more tanks where the doctrine fields any: a light side buys no tank to meet one)
  if (expect?.armour) { w.rocketTechnical = (w.rocketTechnical ?? 0) + 1; if (w.tank) w.tank += 0.5; }
  return w;
}

/**
 * One side's force: `budget` spent on its doctrine's list (re-weighted by `expect`), a kind at a time — the kind whose count
 * over its weight is lowest next (ties: the order of KINDS), while one fits the budget and its caps. `squads` is the side's
 * (its troops in squads): crewed vehicles at most FORCE_BUDGET.crewShare of them, carriers at most one a squad left.
 */
export function composeSide(budget: number, doctrine: Doctrine, squads: number, vehicleStock: number, expect: Expectation | null): ComposedForce {
  const w = weights(doctrine, expect);
  const n: Partial<Record<ForceKind, number>> = {};
  const count = (k: ForceKind): number => n[k] ?? 0;
  const sum = (set: ReadonlySet<ForceKind>): number => KINDS.reduce((a, k) => a + (set.has(k) ? count(k) : 0), 0);
  const price = (k: ForceKind): number => FORCE_PRICE[k] * (VEHICLES.has(k) ? 1 + Math.max(0, Math.floor(vehicleStock)) : 1);
  const crewMax = Math.floor(squads * FORCE_BUDGET.crewShare);
  const fits = (k: ForceKind, left: number): boolean => {
    if (price(k) > left + 1e-9) return false;
    if (CREWED.has(k) && (sum(CREWED) + 1 > crewMax || sum(CARRIERS) > squads - sum(CREWED) - 1)) return false;
    if (CARRIERS.has(k) && sum(CARRIERS) + 1 > squads - sum(CREWED)) return false;
    if (k === 'bike' && count(k) + 1 > squads * FORCE_BUDGET.bikesPerSquad) return false;
    if (k === 'hmg' && count(k) + 1 > squads * FORCE_BUDGET.hmgsPerSquad) return false;
    if (CALLS.has(k) && count(k) + 1 > FORCE_BUDGET.callsMax) return false;
    return true;
  };
  let left = budget;
  for (;;) {
    let pick: ForceKind | null = null, best = Infinity;
    for (const k of KINDS) {
      const wk = w[k] ?? 0;
      if (wk <= 0 || !fits(k, left)) continue;
      const r = count(k) / wk;
      if (r < best - 1e-12) { best = r; pick = k; }
    }
    if (!pick) break;
    n[pick] = count(pick) + 1;
    left -= price(pick);
  }
  // (a carrier never strands a squad a crew took since: a crewed buy needs its squad free of the carriers bought before it)
  const fill = (k: ForceKind, a: MountArmament): MountArmament[] => Array<MountArmament>(count(k)).fill(a);
  const techs = [...fill('technical', 'hmg'), ...fill('rocketTechnical', 'rocket'), ...fill('missileTechnical', 'missile')];
  const tracked = [...fill('tracked', 'hmg'), ...fill('mortarCarrier', 'mortar')];
  return {
    transports: count('transport'), tracked: tracked.length, trackedArmaments: tracked, tanks: count('tank'), technicals: techs.length, technicalArmaments: techs,
    bikes: count('bike'), hmgs: count('hmg'), airstrikes: count('airstrike'), dropships: count('dropship'), pods: count('pod'),
    bought: n, spent: budget - left, budget, doctrine,
  };
}

/** A side's force (composed or authored) as the kinds the composer reads, for the other side's expectation. */
function boughtOf(f: SideForce): Partial<Record<ForceKind, number>> {
  return { transport: f.transports, tracked: f.tracked, tank: f.tanks, technical: f.technicals, bike: f.bikes, hmg: f.hmgs, airstrike: f.airstrikes, dropship: f.dropships, pod: f.pods };
}

/**
 * Every side's force (273: B feeding C, truth by default): each composed blind, then each again against the others' blind
 * compositions. `doctrines[t]` null: side t fields `authored[t]` (A, the override), and the others expect it. `squads[t]` is
 * side t's squads.
 */
export function composeForces(budgets: ForceBudgets, doctrines: readonly (Doctrine | null)[], squads: readonly number[], vehicleStock: number, authored: readonly SideForce[]): Array<ComposedForce | null> {
  const teams = doctrines.map((_, t) => t);
  const blind = teams.map(t => { const d = doctrines[t]; return d ? composeSide(budgets.budgetFor(t), d, squads[t] ?? 0, vehicleStock, null).bought : boughtOf(authored[t]); });
  return teams.map(t => {
    const d = doctrines[t];
    if (!d) return null;
    const enemy: Partial<Record<ForceKind, number>> = {};
    for (const e of teams) if (e !== t) for (const k of KINDS) enemy[k] = (enemy[k] ?? 0) + (blind[e][k] ?? 0);
    return composeSide(budgets.budgetFor(t), d, squads[t] ?? 0, vehicleStock, expects(enemy));
  });
}
